import React from 'react';
import { Line } from 'react-chartjs-2';
import { FinancialTransaction } from '../../services/database/supabase';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Registrar componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface CashFlowChartProps {
  transactions: FinancialTransaction[];
}

export const CashFlowChart: React.FC<CashFlowChartProps> = ({ transactions }) => {
  const calculateChartData = () => {
    // Agrupar transações por data
    const transactionsByDate = transactions.reduce((acc, transaction) => {
      const date = new Date(transaction.date.split('T')[0]);
      const dateStr = date.toLocaleDateString('pt-BR');
      
      if (!acc[dateStr]) {
        acc[dateStr] = {
          income: 0,
          expenses: 0
        };
      }

      if (transaction.type === 'Receita' || transaction.type === 'Salário') {
        acc[dateStr].income += transaction.amount;
      } else if (transaction.type === 'Despesas') {
        acc[dateStr].expenses += transaction.amount;
      }

      return acc;
    }, {} as Record<string, { income: number; expenses: number }>);

    // Ordenar datas
    const sortedDates = Object.keys(transactionsByDate).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    return {
      labels: sortedDates,
      datasets: [
        {
          label: 'Receitas',
          data: sortedDates.map(date => transactionsByDate[date].income),
          borderColor: '#1AD1A5',
          backgroundColor: 'rgba(26, 209, 165, 0.1)',
          borderWidth: 2,
          pointBackgroundColor: '#1AD1A5',
          tension: 0.4,
          fill: true,
        },
        {
          label: 'Despesas',
          data: sortedDates.map(date => transactionsByDate[date].expenses),
          borderColor: '#FF6B6B',
          backgroundColor: 'rgba(255, 107, 107, 0.1)',
          borderWidth: 2,
          pointBackgroundColor: '#FF6B6B',
          tension: 0.4,
          fill: true,
        }
      ]
    };
  };

  return (
    <Line
      data={calculateChartData()}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top' as const,
            align: 'end' as const,
            labels: {
              boxWidth: 8,
              usePointStyle: true,
              pointStyle: 'circle',
              padding: 15,
              color: '#fff'
            }
          },
          tooltip: {
            mode: 'index' as const,
            intersect: false,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderWidth: 1,
            padding: 12,
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: 'rgba(255, 255, 255, 0.05)',
              drawBorder: false
            },
            ticks: { 
              color: '#8E95A3',
              padding: 10,
              callback: function(value) {
                return new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  notation: 'compact',
                  maximumFractionDigits: 1
                }).format(value as number);
              }
            }
          },
          x: {
            grid: {
              display: false
            },
            ticks: {
              color: '#8E95A3',
              padding: 5,
              maxRotation: 0,
              autoSkipPadding: 15
            }
          }
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false
        },
        elements: {
          line: {
            tension: 0.4
          },
          point: {
            radius: 0,
            hoverRadius: 6
          }
        }
      }}
    />
  );
};
